
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  gamesQuery,
  courseMembersStudentNamesQuery,
  consolidatedScoresLeaderboardQuery,
} from '../api';

import './styles.css';

import { sortAndSelectTop } from '../../../../utils';
import SubPagesLayout from '../../../UI/layout/subPages';

import { navigation } from '../data';
import { LeaderboardOverall } from '../../../UI/instructor/leaderboard/overall';

// Map UI filter labels to the game-type keys the ?leaderboard endpoint uses.
const FILTER_TO_KEY = {
  'Classification': 'classification',
  'Normal': 'normal_balance',
  'Double Entry': 'balanced_equation',
  'The Principles': 'principles',
};

export default function InstructorInsightsPoints() {
  const navigate = useNavigate();

  const [courseId, setCourseId] = useState(0);

  // Default to descending (highest points first) rather than a random
  // shuffle so the Top 10 view reads correctly on load. Sort button
  // still lets the instructor flip the direction.
  const [sort, setSort] = useState('desc');
  const [isRandomOrder, setIsRandomOrder] = useState(false);

  const [filterBy, setFilterBy] = useState('Classification');
  const [data, setData] = useState(null);

  const gamesResponse = useQuery(gamesQuery()) || {};
  const games = gamesResponse.data;

  useEffect(() => {
    if (games) {
      let game = games.find(game => game.game_type.includes('Account Classification'));

      if (!game) {
        game = games[0];
      }

      if (game) {
        setCourseId(game.course);
      }
    }
  }, [games]);

  // Pre-computed top-10 leaderboard per metric per game type from the
  // backend — replaces the full session payload + frontend aggregation.
  const { data: leaderboardData } = useQuery(consolidatedScoresLeaderboardQuery());

  // Slim student roster for user_id → name mapping.
  const { data: courseMembers } = useQuery({
    ...courseMembersStudentNamesQuery(),
    enabled: !!courseId,
  });

  const filterByOptions = [...new Set(games?.map(game => {
    switch (game.game_type) {
      case 'Account Classification':
        return 'Classification';
      case 'Normal Balance':
        return 'Normal';
      case 'Balanced Equation':
        return 'Double Entry';
      default:
        return game.game_type;
    }
  }))].sort((a, b) => {
    const order = ['Classification', 'Normal', 'The Principles', 'Double Entry'];
    return order.indexOf(a) - order.indexOf(b);
  });

  const handleSortClick = () => {
    setIsRandomOrder(false);
    setSort(prevSort => (prevSort === 'desc' ? 'asc' : 'desc'));
  };

  const formatLeaderboardData = () => {
    const gameTypeKey = FILTER_TO_KEY[filterBy];
    const entries = leaderboardData?.average_points_sessions?.[gameTypeKey] || [];

    const wholeLeaderboardData = entries.map(entry => {
      const member = courseMembers.find(m => m.user_id === entry.user_id);
      return {
        name: member?.name || 'Student',
        score: entry.value,
        formattedScore: entry.value,
      };
    });

    const sortedLeaderboardData = sortAndSelectTop(wholeLeaderboardData, sort, isRandomOrder)
      .map((item, index) => ({
        ...item,
        backgroundColor: index < 3 ? '#5C91F5' : index < 6 ? '#A98DF6' : undefined
      }));

    setData(sortedLeaderboardData);
  }

  useEffect(() => {
    if(leaderboardData && courseMembers) {
      if(filterByOptions[0] !== 'Classification') {
        setFilterBy(filterByOptions[0]);
      }

      formatLeaderboardData();
    }
  }, [leaderboardData, courseMembers]);

  useEffect(() => {
    if(leaderboardData && courseMembers) {
      formatLeaderboardData();
    }
  }, [filterBy, sort]);

  const handleNextButtonClick = () => {
    navigate('/instructor/insights/leaderboard/accuracy');
  }

  const handleFilterClick = (selectedFilterBy) => {
    setIsRandomOrder(false);
    setSort('desc');
    setFilterBy(selectedFilterBy);
  }
  
  const maxScore = data ? data.reduce((max, item) => (item.score > max ? item.score : max), -Infinity) : 0;

  return (
    <SubPagesLayout
      navigation={navigation}
      contentClassName="insights-content-container"
      onSecondButtonClick={handleNextButtonClick}
    >
      <h1>Insights | Leaderboard</h1>
      <h2>Points</h2>
      <p>Points are driven by how quickly students make correct decisions.</p>
        <LeaderboardOverall
          title="Top 10 Performers"
          subtitle="Points"
          data={data}
          filterBy={filterBy}
          filterByOptions={filterByOptions}
          onFilterByClick={handleFilterClick}
          maxScore={maxScore}
          sort={sort}
          onSortClick={handleSortClick}
        />
    </SubPagesLayout>
  );
}
